// Filtrado, orden y búsqueda del catálogo del paso 03 — TODO en el cliente, a propósito.
//
// 🔴 POR QUÉ CLIENT-SIDE. El mock (`wizard/03-producto.html`, bloque "For Dev") lo declara como
// contrato: *"Backend devuelve TODOS los productos de la tienda en una sola llamada · volumen
// esperado 20-60 SKUs · Frontend filtra y ordena client-side (busqueda instantánea)"*.
//
// Antes se mandaba `?q=` y `?sort=` al silo y se refetcheaba en CADA TECLA. Dos problemas, no uno:
// el backend NUNCA leyó esos parámetros (`GET /wizard/catalog` solo mira `storeId`), así que la
// búsqueda no filtraba nada; y como `q` viajaba en el query key de TanStack, cada letra era un
// cache miss que blanqueaba la grilla y pagaba tres saltos de lambda (authorizer → retail-org →
// retail-catalog). Escribir "iphone" costaba 6 round-trips para devolver 6 veces la misma lista.
//
// Con el catálogo entero en memoria, filtrar es un `Array.filter` y la búsqueda es instantánea.
import { isService, isSoldOut, stockCount } from './catalogAvailability';
import type { CatalogItem } from './types';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { financierName } from '@/domains/credit/origination/lib/originationMetrics';
import { variantCapacityLabel } from '@/shared/lib/format/capacity';

/**
 * Centinela de "sin filtro".
 *
 * ⚠️ NO se puede usar `''`: Radix (el primitivo abajo de `<Select>` del kit) revienta con
 * *"A <Select.Item /> must have a value prop that is not an empty string"*.
 */
export const SIN_FILTRO = 'ALL';

export type CatalogSort =
    | 'recommended'
    | 'promo'
    | 'price-asc'
    | 'price-desc'
    | 'stock-desc'
    | 'brand-asc';

/** Los órdenes del mock, en su mismo orden. */
export const CATALOG_SORTS: { value: CatalogSort; label: string }[] = [
    { value: 'recommended', label: 'Recomendados' },
    { value: 'promo', label: 'Con promoción' },
    { value: 'price-asc', label: 'Precio: menor a mayor' },
    { value: 'price-desc', label: 'Precio: mayor a menor' },
    { value: 'stock-desc', label: 'Más disponibles' },
    { value: 'brand-asc', label: 'Marca A–Z' },
];

/**
 * "Con promoción" vive en el dropdown de orden pero ESCONDE los equipos sin promoción: es un
 * filtro disfrazado de orden, igual que en el mock. Dentro, ordena como "Recomendados".
 */
export function showsOnlyPromoted(sort: CatalogSort): boolean {
    return sort === 'promo';
}

export interface PriceBand {
    value: string;
    label: string;
    /** Inclusivo, en centavos. */
    minCents: number;
    /** Inclusivo, en centavos. */
    maxCents: number;
}

/** Bandas de precio del mock, en CENTAVOS (que es como viaja `cashPriceCents`). */
export const PRICE_BANDS: PriceBand[] = [
    {
        value: SIN_FILTRO,
        label: 'Cualquier precio',
        minCents: 0,
        maxCents: Number.POSITIVE_INFINITY,
    },
    { value: 'lt4k', label: 'Menos de $4,000', minCents: 0, maxCents: 399_999 },
    { value: '4k7k', label: '$4,000 – $7,000', minCents: 400_000, maxCents: 699_999 },
    { value: '7k15k', label: '$7,000 – $15,000', minCents: 700_000, maxCents: 1_499_999 },
    {
        value: 'gt15k',
        label: 'Más de $15,000',
        minCents: 1_500_000,
        maxCents: Number.POSITIVE_INFINITY,
    },
];

export interface CatalogFilterState {
    q: string;
    /** Marca exacta, o `SIN_FILTRO`. */
    brand: string;
    /** `PriceBand.value`. */
    band: string;
    /** Código de financiera de `eligibleFinanciers`, o `SIN_FILTRO`. */
    financier: string;
    onlyInStock: boolean;
    sort: CatalogSort;
}

export const FILTROS_VACIOS: CatalogFilterState = {
    q: '',
    brand: SIN_FILTRO,
    band: SIN_FILTRO,
    financier: SIN_FILTRO,
    onlyInStock: false,
    sort: 'recommended',
};

/** ¿Hay algo que "limpiar"? Del dropdown de orden solo cuenta "Con promoción", el único que esconde. */
export function hayFiltrosActivos(f: CatalogFilterState): boolean {
    return (
        f.q.trim() !== '' ||
        f.brand !== SIN_FILTRO ||
        f.band !== SIN_FILTRO ||
        f.financier !== SIN_FILTRO ||
        f.onlyInStock ||
        showsOnlyPromoted(f.sort)
    );
}

/** Marcas presentes en el catálogo, alfabéticas — alimentan el `<Select>` de marca. */
export function marcasDe(items: CatalogItem[]): string[] {
    return [...new Set(items.map((i) => i.brand))]
        .filter((b) => b !== '')
        .sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/** Financieras presentes en el catálogo, sin repetir y en orden alfabético por nombre visible. */
export function financiersOf(items: CatalogItem[]): string[] {
    return [...new Set(items.flatMap((i) => i.eligibleFinanciers))].sort((a, b) =>
        financierName(a).localeCompare(financierName(b), 'es-MX'),
    );
}

/** Nombres visibles de las financieras, en orden alfabético. */
export function financierNamesOf(codes: string[]): string[] {
    return codes.map(financierName).sort((a, b) => a.localeCompare(b, 'es-MX'));
}

/**
 * Minúsculas SIN acentos, **preservando la longitud del string**.
 *
 * Lo normal sería `normalize('NFD').replace(/\p{Diacritic}/gu, '')`, pero eso CAMBIA la longitud y
 * rompe el resaltado de coincidencias (`rangosDeCoincidencia` mapea índices 1:1 contra el texto
 * original). Un reemplazo carácter a carácter mantiene la correspondencia.
 */
export function plegar(texto: string): string {
    return texto.toLowerCase().replace(/[áàäâéèëêíìïîóòöôúùüûñç]/g, (c) => PLIEGUE[c] ?? c);
}

const PLIEGUE: Record<string, string> = {
    á: 'a',
    à: 'a',
    ä: 'a',
    â: 'a',
    é: 'e',
    è: 'e',
    ë: 'e',
    ê: 'e',
    í: 'i',
    ì: 'i',
    ï: 'i',
    î: 'i',
    ó: 'o',
    ò: 'o',
    ö: 'o',
    ô: 'o',
    ú: 'u',
    ù: 'u',
    ü: 'u',
    û: 'u',
    ñ: 'n',
    ç: 'c',
};

/** Los términos de la búsqueda, ya plegados. `""` → `[]` (sin filtro). */
export function terminosDe(q: string): string[] {
    return plegar(q)
        .split(/\s+/)
        .filter((t) => t !== '');
}

/**
 * ¿El equipo matchea la búsqueda? **Todos** los términos tienen que aparecer en algún campo.
 *
 * Es lo que hace que "sam 256" encuentre el Galaxy A16 de 256GB: el vendedor teclea como habla, no
 * como está escrito el `model`. La capacidad, el color y el SKU entran al índice por lo mismo — dos
 * variantes del mismo modelo solo se distinguen por ahí.
 */
export function coincide(item: CatalogItem, terminos: string[]): boolean {
    if (terminos.length === 0) return true;
    const indice = plegar(
        `${itemDisplayName(item)} ${item.capacity} ${variantCapacityLabel(item)} ${item.color} ${item.sku}`,
    );
    return terminos.every((t) => indice.includes(t));
}

export function filtrarCatalogo(items: CatalogItem[], f: CatalogFilterState): CatalogItem[] {
    const terminos = terminosDe(f.q);
    const banda = PRICE_BANDS.find((b) => b.value === f.band) ?? PRICE_BANDS[0];
    return items.filter((i) => {
        if (!coincide(i, terminos)) return false;
        if (f.brand !== SIN_FILTRO && i.brand !== f.brand) return false;
        if (i.cashPriceCents < banda.minCents || i.cashPriceCents > banda.maxCents) return false;
        if (f.financier !== SIN_FILTRO && !i.eligibleFinanciers.includes(f.financier)) return false;
        if (f.onlyInStock && isSoldOut(i)) return false;
        if (showsOnlyPromoted(f.sort) && !i.hasPromo) return false;
        return true;
    });
}

// salePriority mayor primero y antes que los que no la traen; sin ella, gama y existencia.
const bySalePriority = (a: CatalogItem, b: CatalogItem): number =>
    (b.salePriority ?? -1) - (a.salePriority ?? -1);

const byPriority = (a: CatalogItem, b: CatalogItem): number =>
    bySalePriority(a, b) || b.priorityRank - a.priorityRank || stockCount(b) - stockCount(a);

const COMPARADORES: Record<CatalogSort, (a: CatalogItem, b: CatalogItem) => number> = {
    recommended: byPriority,
    promo: byPriority,
    'price-asc': (a, b) => a.cashPriceCents - b.cashPriceCents,
    'price-desc': (a, b) => b.cashPriceCents - a.cashPriceCents,
    'stock-desc': (a, b) => stockCount(b) - stockCount(a),
    'brand-asc': (a, b) => itemDisplayName(a).localeCompare(itemDisplayName(b), 'es-MX'),
};

/**
 * Ordena SIN mutar el arreglo de entrada (viene del caché de TanStack Query — mutarlo corrompería
 * la caché compartida).
 *
 * 🔴 **Lo agotado SIEMPRE va al final**, sin importar el orden elegido. Un equipo sin inventario no
 * se puede vender: ocupando los primeros lugares le roba la pantalla a los que sí. Esto NO es el
 * filtro "Solo con inventario" — ese los esconde; este solo los baja.
 */
export function ordenarCatalogo(items: CatalogItem[], sort: CatalogSort): CatalogItem[] {
    const cmp = COMPARADORES[sort];
    return [...items].sort((a, b) => Number(isSoldOut(a)) - Number(isSoldOut(b)) || cmp(a, b));
}

export function aplicarFiltros(items: CatalogItem[], f: CatalogFilterState): CatalogItem[] {
    return ordenarCatalogo(filtrarCatalogo(items, f), f.sort);
}

export type StockTone = 'success' | 'warning' | 'danger';

/** Chip de inventario. `< 5` es ámbar porque es una señal de urgencia, no de error. */
export function chipDeStock(stock: number): { tone: StockTone; label: string } {
    if (stock <= 0) return { tone: 'danger', label: 'Sin inventario' };
    if (stock < 5) return { tone: 'warning', label: `Últimos ${String(stock)}` };
    return { tone: 'success', label: `${String(stock)} disponibles` };
}

/** Chip de un SKU: un servicio no lleva inventario y nunca se muestra agotado. */
export function chipDe(item: Pick<CatalogItem, 'itemType' | 'stock'>): {
    tone: StockTone;
    label: string;
} {
    if (isService(item)) return { tone: 'success', label: 'Siempre disponible' };
    return chipDeStock(item.stock ?? 0);
}

/**
 * Tramos `[inicio, fin)` del texto que matchean algún término, ya fusionados y ordenados.
 * Los índices son del texto ORIGINAL (ver `plegar`), así que se puede cortar con `slice`.
 */
export function rangosDeCoincidencia(texto: string, terminos: string[]): [number, number][] {
    if (terminos.length === 0) return [];
    const plegado = plegar(texto);
    const crudos: [number, number][] = [];
    for (const t of terminos) {
        let desde = plegado.indexOf(t);
        while (desde !== -1) {
            crudos.push([desde, desde + t.length]);
            desde = plegado.indexOf(t, desde + 1);
        }
    }
    crudos.sort((a, b) => a[0] - b[0]);
    return crudos.reduce<[number, number][]>((acc, [ini, fin]) => {
        const previo = acc.at(-1);
        if (previo && ini <= previo[1]) {
            previo[1] = Math.max(previo[1], fin);
            return acc;
        }
        acc.push([ini, fin]);
        return acc;
    }, []);
}
